import React, { memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';

import { CountryConfigNames } from '../../../contexts/country-config-context/country-config-enums';
import { useCountryConfig } from '../../../hooks/use-country-config';
import { EventSourceStrings } from '../../../tracking/tracking-constants';
import { WalletRegistrationOrUpdateButton } from '../../../wallet/components/wallet-registration-or-update-button';
import { useWallet } from '../../../wallet/hooks/use-wallet';
import { getAllRatesFromRateGroups } from '../rate-groups-service';
import { NewRateListItem } from '../rate-list/rate-list-item/new-rate-list-item';
import { useRateViewerContext } from '../rate-viewer-context-provider';
import { AllRatesViewErrorBanner } from './all-rates-view-error-banner';

import { messages } from '../messages';
import style from './all-rates-view.module.css';

const isRateSelected = (currentRate, currentlySelectedRate) => {
  if (!currentlySelectedRate) {
    return false;
  }
  return (
    currentRate.sellerLabelProviderId === currentlySelectedRate.sellerLabelProviderId &&
    currentRate.serviceId === currentlySelectedRate.serviceId &&
    currentRate.packageTypeId === currentlySelectedRate.packageTypeId
  );
};

export const AllRatesView = memo(() => {
  const { isWalletRegistered } = useWallet();
  const [requiresWalletForRateBrowser] = useCountryConfig(CountryConfigNames.RequiresWalletForRateBrowser);
  const showWalletRequiredMessage = requiresWalletForRateBrowser && !isWalletRegistered;
  const { filteredRateGroups, selectedRate, updateSelectedRate, shouldShowLoadingIndicator, shouldShowAllRatesError, validationErrors } =
    useRateViewerContext();

  const rates = useMemo(() => {
    const allRates = getAllRatesFromRateGroups(filteredRateGroups);
    const seen = new Set();

    // Deduplicate: keep first occurrence of each unique rate
    // Service collection rates come first, so they take precedence
    // however we want to show duplicate rates if it's a service collection rate
    return allRates.filter((rate) => {
      const key = `${rate.sellerLabelProviderId}-${rate.serviceId}-${rate.packageTypeId}`;
      if (!rate.isServiceCollectionRate && seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [filteredRateGroups]);

  if (shouldShowLoadingIndicator) {
    return (
      <div className={style['loading-indicator-container']}>
        <LoadingIndicator className={style['loading-indicator']} iconClassName={style['loading-indicator-icon']} />
        <Text textType={TextType.H3} value={messages.fetchingRates} />
      </div>
    );
  }

  const shouldShowValidationErrors = !shouldShowAllRatesError && validationErrors?.length > 0;
  const shouldShowEmptyMessage = rates.length === 0;
  const shouldShowRateList = rates.length > 0;

  return (
    <>
      {shouldShowAllRatesError ? <AllRatesViewErrorBanner /> : null}
      {shouldShowValidationErrors ? (
        <BannerMessageV2
          content={validationErrors.map((error, index) => (
            <Text key={index} textType={TextType.Paragraph} value={error} />
          ))}
          status={BannerStatus.Warning}
        />
      ) : null}
      {shouldShowEmptyMessage ? (
        <div className={style['empty-message']}>
          {showWalletRequiredMessage ? (
            <>
              <Text textType={TextType.H4} value={messages.walletRequiredMessage} />
              <WalletRegistrationOrUpdateButton trackingId={EventSourceStrings.RATE_BROWSER_EMPTY_STATE}>
                {({ openModal }) => (
                  <Button className={style['wallet-activation-button']} onClick={openModal} theme='primary'>
                    <FormattedMessage {...messages.activateWallet} />
                  </Button>
                )}
              </WalletRegistrationOrUpdateButton>
            </>
          ) : (
            <>
              <Text textType={TextType.H4} value={messages.noRates} />
              <Text textType={TextType.Caption} value={messages.needInfoMessage} />
            </>
          )}
        </div>
      ) : null}
      {shouldShowRateList ? (
        <div className={style['rate-list']}>
          {rates.map((rate, index) => (
            <NewRateListItem key={index} isSelected={isRateSelected(rate, selectedRate)} onClick={updateSelectedRate} rate={rate} />
          ))}
        </div>
      ) : null}
    </>
  );
});
